<template>
  <!-- Custom trigger slot: parent provides the trigger; we wrap it so click opens panel and we have ref for positioning -->
  <div v-if="$slots.trigger" class="ots-kpi-popover-wrapper relative inline-block w-full min-w-0" ref="wrapperRef">
    <div ref="triggerRef" class="w-full min-w-0" @click="toggle">
      <slot name="trigger" :open="isOpen" :toggle="toggle" />
    </div>
  </div>
  <!-- Default trigger: built-in button with caret -->
  <div v-else class="ots-kpi-popover-wrapper relative inline-block" ref="wrapperRef">
    <button
      type="button"
      ref="triggerRef"
      class="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-lg border border-border hover:bg-surface-hover transition-colors min-w-0 cursor-pointer text-sm"
      :aria-expanded="isOpen"
      :aria-controls="panelId"
      aria-haspopup="true"
      aria-label="On-Time Shipping Metrics summary"
      @click="toggle"
      @keydown="onTriggerKeydown"
    >
      <i class="ri-bar-chart-line w-4 h-4 flex items-center justify-center text-blue-400 shrink-0" aria-hidden="true" />
      <span class="flex flex-wrap items-center gap-x-1.5 gap-y-0">
        <span class="text-text-secondary">Total</span>
        <span :class="otsStatusClass(blocks.total)" class="font-semibold">{{ blocks.total.pct != null ? formatOtsPct(blocks.total.pct) + '%' : 'N/A' }}</span>
        <span class="text-text-muted" aria-hidden="true">|</span>
        <span class="text-text-secondary">EVT</span>
        <span :class="otsStatusClass(blocks.evt)" class="font-semibold">{{ blocks.evt.pct != null ? formatOtsPct(blocks.evt.pct) + '%' : 'N/A' }}</span>
        <span class="text-text-muted" aria-hidden="true">|</span>
        <span class="text-text-secondary">LAN</span>
        <span :class="otsStatusClass(blocks.lan)" class="font-semibold">{{ blocks.lan.pct != null ? formatOtsPct(blocks.lan.pct) + '%' : 'N/A' }}</span>
      </span>
      <i
        class="ri-arrow-down-s-line w-4 h-4 shrink-0 text-text-secondary transition-transform duration-200"
        :class="{ 'rotate-180': isOpen }"
        aria-hidden="true"
      />
    </button>
  </div>

  <Teleport to="body">
      <Transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="panelVisible"
          :id="panelId"
          ref="panelRef"
          role="dialog"
          aria-labelledby="ots-kpi-panel-title"
          class="ots-kpi-popover-panel fixed z-[10000] flex min-h-0 w-80 sm:w-[26rem] flex-col overflow-hidden rounded-xl border border-border bg-surface-muted shadow-2xl"
          @keydown="onPanelKeydown"
        >
          <!-- Header: title + close; then date row -->
          <div class="shrink-0 px-5 py-4 border-b border-border bg-surface">
            <div class="flex items-center justify-between gap-2">
              <div class="flex items-center gap-2 min-w-0">
                <i class="ri-bar-chart-box-line text-lg text-blue-400 shrink-0" aria-hidden="true" />
                <h2 id="ots-kpi-panel-title" class="text-sm font-semibold text-text truncate">
                  On-Time Shipping Metrics
                </h2>
              </div>
              <button
                type="button"
                class="w-6 h-6 flex items-center justify-center rounded hover:bg-surface-hover text-text-muted transition-colors cursor-pointer"
                aria-label="Close"
                @click="close"
              >
                <i class="ri-close-line" aria-hidden="true" />
              </button>
            </div>
            <div v-if="displayDateFormatted" class="flex items-center gap-1.5 mt-2 text-xs text-text-secondary">
              <i class="ri-calendar-line w-3.5 h-3.5 flex items-center justify-center shrink-0" aria-hidden="true" />
              <span>{{ displayDateFormatted }}</span>
            </div>
            <!-- Order population selector: nav bar has limited space, so choose one at a time. -->
            <div class="mt-3 grid grid-cols-3 gap-1 rounded-lg bg-surface-muted p-0.5" role="group" aria-label="Order population">
              <button
                v-for="opt in populationOptions"
                :key="opt.value"
                type="button"
                class="px-2 py-1 text-xs font-medium rounded-md transition-colors cursor-pointer"
                :class="population === opt.value ? 'bg-surface text-text shadow-sm' : 'text-text-muted hover:text-text'"
                :aria-pressed="population === opt.value"
                @click="emit('update:population', opt.value)"
              >
                {{ opt.label }}
              </button>
            </div>
          </div>

          <!-- Cards (scroll when panel hits viewport max-height) -->
          <div class="min-h-0 flex-1 overflow-y-auto p-5 space-y-4">
            <OtsKpiSegmentCard v-for="block in blockList" :key="block.label" :block="block" />
          </div>

          <!-- Footer (same gate as OTS report route and nav link) -->
          <div
            v-if="canViewOtsReport && (reportUrl || onViewReport)"
            class="shrink-0 px-5 py-3 border-t border-border bg-surface"
          >
            <RouterLink
              v-if="reportUrl"
              :to="reportUrl"
              class="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-blue-400 transition-colors whitespace-nowrap hover:bg-surface-hover"
              @click="close"
            >
              <span>View Full Report</span>
              <i class="ri-arrow-right-line" aria-hidden="true" />
            </RouterLink>
            <button
              v-else-if="onViewReport"
              type="button"
              class="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-blue-400 transition-colors whitespace-nowrap hover:bg-surface-hover"
              @click="handleViewReport"
            >
              <span>View Full Report</span>
              <i class="ri-arrow-right-line" aria-hidden="true" />
            </button>
          </div>
        </div>
      </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { RouterLink } from 'vue-router'
import OtsKpiSegmentCard from '@/components/operations/OtsKpiSegmentCard.vue'
import { usePermissions } from '@/composables/usePermissions'
import { PERMISSIONS } from '@/constants/permissions'
import type { PrintIqKpiSuccess } from '@/types/printiqKpi'
import type { OtsOrderPopulation } from '@/services/machineService'
import { formatOtsPct, kpiToBlocks, otsStatusClass, type OtsKpiBlock } from '@/utils/otsKpiPresentation'

export type { OtsKpiBlock } from '@/utils/otsKpiPresentation'

interface Props {
  /** Full KPI data from store; used to derive total/evt/lan blocks. */
  kpi?: PrintIqKpiSuccess | null
  /** Override: pass blocks explicitly instead of kpi (kpi takes precedence if both set). */
  total?: OtsKpiBlock
  evt?: OtsKpiBlock
  lan?: OtsKpiBlock
  /** Selected order population (drives the in-panel selector's active state). */
  population?: OtsOrderPopulation
  /** Report URL for "View Full Report" link. Default: /operations/reports/on-time-shipping */
  reportUrl?: string
  /** Optional callback when "View Full Report" is clicked (used when reportUrl not provided). */
  onViewReport?: () => void
}

const props = withDefaults(defineProps<Props>(), {
  kpi: undefined,
  population: 'standard_custom',
  reportUrl: '/operations/reports/on-time-shipping'
})

const emit = defineEmits<{ (e: 'update:population', value: OtsOrderPopulation): void }>()

const populationOptions: { value: OtsOrderPopulation; label: string }[] = [
  { value: 'combined', label: 'Combined' },
  { value: 'standard_custom', label: 'Std/Custom' },
  { value: 'series', label: 'Series/Launch/Init' },
]

const { hasPermission } = usePermissions()
const canViewOtsReport = computed(() => hasPermission(PERMISSIONS.OPERATIONS.ON_TIME_SHIPPING.VIEW))

const blocks = computed(() => {
  if (props.kpi != null) {
    return kpiToBlocks(props.kpi)
  }
  const total = props.total ?? { label: 'TOTAL' as const, pct: 0, shipped: 0, onTime: 0 }
  const evt = props.evt ?? { label: 'EVT' as const, pct: 0, shipped: 0, onTime: 0 }
  const lan = props.lan ?? { label: 'LAN' as const, pct: 0, shipped: 0, onTime: 0 }
  return { total, evt, lan }
})

const blockList = computed(() => [blocks.value.total, blocks.value.evt, blocks.value.lan])

/** Formatted date for panel header: compact from–to range (e.g. "Jul 24 – Aug 6, 2026") from kpi.date_from/date_to. */
const displayDateFormatted = computed(() => {
  const from = props.kpi?.date_from
  const to = props.kpi?.date_to
  if (!from) return ''
  try {
    const fromD = new Date(from + 'T12:00:00')
    if (Number.isNaN(fromD.getTime())) return ''
    const toD = to ? new Date(to + 'T12:00:00') : null
    if (!toD || Number.isNaN(toD.getTime())) {
      return fromD.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    }
    const fromStr = fromD.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const toStr = toD.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    return `${fromStr} – ${toStr}`
  } catch {
    return ''
  }
})

const panelId = 'ots-kpi-popover-panel'
const isOpen = ref(false)
const wrapperRef = ref<HTMLElement | null>(null)
const triggerRef = ref<HTMLElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)

/** Panel visibility: always follows internal isOpen (trigger slot or default button both use toggle()). */
const panelVisible = computed(() => isOpen.value)

/** Viewport inset for clamping (matches horizontal panel margin). */
const PANEL_VIEWPORT_MARGIN = 8
/** Gap between trigger bottom and panel top when space allows. */
const PANEL_TRIGGER_GAP = 4

function getViewportSize(): { width: number; height: number } {
  const vv = window.visualViewport
  return {
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight
  }
}

function toggle() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    nextTick(() => {
      requestAnimationFrame(() => {
        positionPanel()
      })
    })
  }
}

function close() {
  isOpen.value = false
}

function positionPanel() {
  const triggerEl = triggerRef.value
  if (!triggerEl || !panelRef.value) return
  const rect = triggerEl.getBoundingClientRect()
  const panel = panelRef.value
  const { width: viewportWidth, height: viewportHeight } = getViewportSize()
  const maxPanelHeight = Math.max(0, viewportHeight - 2 * PANEL_VIEWPORT_MARGIN)

  panel.style.position = 'fixed'
  panel.style.zIndex = '10000'
  panel.style.maxHeight = `${maxPanelHeight}px`
  panel.style.left = `${rect.left}px`
  panel.style.right = 'auto'
  panel.style.width = 'min(26rem, calc(100vw - 1rem))'

  const maxLeft = viewportWidth - panel.offsetWidth - PANEL_VIEWPORT_MARGIN
  if (rect.left > maxLeft) {
    panel.style.left = `${Math.max(PANEL_VIEWPORT_MARGIN, maxLeft)}px`
  } else if (rect.left < PANEL_VIEWPORT_MARGIN) {
    panel.style.left = `${PANEL_VIEWPORT_MARGIN}px`
  }

  let top = rect.bottom + PANEL_TRIGGER_GAP
  const panelHeight = panel.getBoundingClientRect().height
  if (top + panelHeight > viewportHeight - PANEL_VIEWPORT_MARGIN) {
    top = viewportHeight - PANEL_VIEWPORT_MARGIN - panelHeight
  }
  if (top < PANEL_VIEWPORT_MARGIN) {
    top = PANEL_VIEWPORT_MARGIN
  }
  panel.style.top = `${top}px`
}

function handleViewReport() {
  props.onViewReport?.()
  close()
}

function onTriggerKeydown(e: KeyboardEvent) {
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault()
    toggle()
  } else if (e.key === 'Escape' && isOpen.value) {
    close()
  }
}

function onPanelKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    close()
  }
}

function handleClickOutside(event: Event) {
  const target = event.target as Element
  if (!wrapperRef.value?.contains(target) && !panelRef.value?.contains(target)) {
    close()
  }
}

function handleResize() {
  if (panelVisible.value) {
    positionPanel()
  }
}

function handleVisualViewportChange() {
  if (panelVisible.value) {
    positionPanel()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('resize', handleResize)
  window.visualViewport?.addEventListener('resize', handleVisualViewportChange)
  window.visualViewport?.addEventListener('scroll', handleVisualViewportChange)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('resize', handleResize)
  window.visualViewport?.removeEventListener('resize', handleVisualViewportChange)
  window.visualViewport?.removeEventListener('scroll', handleVisualViewportChange)
})
</script>
